import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { alertaLoaderHTML } from 'shared-tsi';
import { filter } from 'rxjs';

const UPDATE_MESSAGE = `
  <div><img src="assets/images/logo-tsi-tres.png" class="img-login"></div>
  <div style="font-size: 1.1rem; font-weight: bolder">
    El sistema fue actualizado. La página se recargará.
  </div>`;

export const VERSION_KEY = 'tsi-app-version';

const CHUNK_RETRY_KEY = 'tsi-chunk-retry';

const SAFE_ROUTES = ['/login', '/seleccionar-sucursal', '/recuperar-clave', '/clave-expirada'];

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i;

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {

  private swUpdate = inject(SwUpdate)
  private router = inject(Router)

  readonly updateAvailable = signal(false)

  init(): void {
    this.checkServerVersion(true)
    this.watchNavigation()

    if (!this.swUpdate.isEnabled) return

    this.swUpdate.unrecoverable.subscribe(() => this.showUpdateAndReload())

    this.swUpdate.versionUpdates.pipe(
      filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY')
    ).subscribe(() => this.markUpdateAvailable())

    navigator.serviceWorker?.ready.then(() => {
      this.swUpdate.checkForUpdate().catch(() => { })
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkServerVersion(false)
        this.swUpdate.checkForUpdate().catch(() => { })
      }
    })
  }

  async applyUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      await this.swUpdate.activateUpdate().catch(() => { })
    }
    window.location.reload()
  }

  private markUpdateAvailable(): void {
    this.updateAvailable.set(true)
    if (this.isSafeRoute(this.router.url)) this.applyUpdate()
  }

  private watchNavigation(): void {
    this.router.events.subscribe(e => {
      if (e instanceof NavigationStart && this.updateAvailable() && this.path(e.url) !== this.path(this.router.url)) {
        window.location.assign(e.url)
        return
      }
      if (e instanceof NavigationEnd) {
        this.clearChunkRetry()
        return
      }
      if (e instanceof NavigationError && CHUNK_ERROR.test(String(e.error?.message ?? e.error))) {
        this.retryChunkNavigation(e.url)
      }
    })
  }

  private retryChunkNavigation(url: string): void {
    try {
      if (sessionStorage.getItem(CHUNK_RETRY_KEY) === url) return
      sessionStorage.setItem(CHUNK_RETRY_KEY, url)
    } catch { }
    window.location.assign(url)
  }

  private clearChunkRetry(): void {
    try { sessionStorage.removeItem(CHUNK_RETRY_KEY) } catch { }
  }

  private isSafeRoute(url: string): boolean {
    const path = this.path(url)
    return SAFE_ROUTES.some(route => path === route || path.startsWith(`${route}/`))
  }

  private path(url: string): string {
    return url.split(/[?#]/)[0]
  }

  private async checkServerVersion(isStartup: boolean): Promise<void> {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) return
      const { version } = await res.json()
      if (!version) return

      const stored = localStorage.getItem(VERSION_KEY)
      localStorage.setItem(VERSION_KEY, version)

      if (!stored || stored === version) return

      if (isStartup) {
        await this.clearCachesAndUnregister()
        this.showUpdateAndReload()
        return
      }
      this.markUpdateAvailable()
    } catch { }
  }

  private showUpdateAndReload(): void {
    alertaLoaderHTML(UPDATE_MESSAGE, 3000, () => this.hardReload())
  }

  private async clearCachesAndUnregister(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
    } catch {}
  }

  private async hardReload(): Promise<void> {
    await this.clearCachesAndUnregister();
    window.location.reload();
  }
}
